import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bcgMatrixResponseSchema,
  bcgThresholdsResponseSchema,
  type BcgBubble,
  type BcgMatrixResponse,
  type BcgThresholds,
  type BcgThresholdsResponse,
  type PeriodInput,
} from "@msc/shared";
import { api } from "../lib/api.js";

// ── DTOs ─────────────────────────────────────────────────────────────────────
//
// Die Shapes wohnen als Zod-Schema in `packages/shared` und werden hier und im
// Backend aus derselben Quelle benutzt. Der `parse` beim Lesen ist Absicht:
// Er fängt eine auseinanderlaufende Antwort dort, wo sie entsteht, statt
// später als `undefined` im Chart.

/** Alias: Die Blase heißt in der Oberfläche seit jeher „Customer". */
export type BcgCustomer = BcgBubble;
export type { BcgMatrixResponse, BcgThresholds, BcgThresholdsResponse };

export interface UpdateThresholdPayload {
  segmentId: string;
  period: PeriodInput;
  revenueThreshold?: number | null;
  growthThreshold?: number | null;
}

// ── Query Keys ────────────────────────────────────────────────────────────────

export const bcgKeys = {
  all: ["bcg"] as const,
  matrix: (opts: {
    segmentId: string;
    period: PeriodInput;
    includeInactive: boolean;
    granularity?: "customer" | "engagement";
  }) => [...bcgKeys.all, "matrix", opts] as const,
  thresholds: (opts: { segmentId: string; period: PeriodInput }) =>
    [...bcgKeys.all, "thresholds", opts] as const,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function periodToQueryParams(period: PeriodInput): URLSearchParams {
  const params = new URLSearchParams();
  params.set("periodKind", period.kind);
  if (period.kind === "fiscal_year") {
    params.set("fiscalYearLabel", period.fiscalYearLabel);
  } else if (period.kind === "custom") {
    params.set("periodStart", period.start);
    params.set("periodEnd", period.end);
  }
  return params;
}

// ── Query Hooks ───────────────────────────────────────────────────────────────

export interface UseBcgMatrixOpts {
  segmentId: string;
  period: PeriodInput;
  includeInactive: boolean;
  /** Blase pro Zelle (default) oder pro Beauftragung. */
  granularity?: "customer" | "engagement";
}

export function useBcgMatrix(opts: UseBcgMatrixOpts) {
  const params = periodToQueryParams(opts.period);
  params.set("segmentId", opts.segmentId);
  params.set("includeInactive", String(opts.includeInactive));
  params.set("granularity", opts.granularity ?? "customer");
  const qs = params.toString();

  return useQuery<BcgMatrixResponse>({
    queryKey: bcgKeys.matrix(opts),
    queryFn: async () =>
      bcgMatrixResponseSchema.parse(await api.get(`/bcg/matrix?${qs}`)),
    enabled: Boolean(opts.segmentId),
  });
}

export interface UseBcgThresholdsOpts {
  segmentId: string;
  period: PeriodInput;
}

export function useBcgThresholds(opts: UseBcgThresholdsOpts) {
  const params = periodToQueryParams(opts.period);
  params.set("segmentId", opts.segmentId);
  const qs = params.toString();

  return useQuery<BcgThresholdsResponse>({
    queryKey: bcgKeys.thresholds(opts),
    queryFn: async () =>
      bcgThresholdsResponseSchema.parse(await api.get(`/bcg/thresholds?${qs}`)),
    enabled: Boolean(opts.segmentId),
  });
}

// ── Mutation Hooks ────────────────────────────────────────────────────────────

export function useUpdateThreshold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateThresholdPayload) =>
      api.put<BcgThresholdsResponse>("/bcg/thresholds", payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bcgKeys.all });
    },
  });
}
